import React, { useState, useEffect } from 'react';
import {
	Button,
	Col,
	Row,
	ModalBody,
	Form,
	Spinner,
	FormGroup,
	Label,
	Input,
	FormFeedback,
} from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { DateInput } from '@mantine/dates';
import dayjs from 'dayjs';
import CustomTimeInput from '../../../../Components/Common/CustomTimeInput';
import { updateMeasurementDetail } from '../../../../slices/measurementDetail/thunk';
import { setRequireCloseModal } from '../../../../slices/measurementDetail/reducer';
import { uploadFiles } from '../../../../helpers/file_manager/backend_helper';

const MeasurementDetailsForm = ({
	dataMeasurementDetail,
	handleToggleModal,
	isOpen,
	toggleRefetch,
}) => {
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState({});

	const [measurementDate, setMeasurementDate] = useState(null);
	const [measurementTime, setMeasurementTime] = useState('');
	const [totalizer, setTotalizer] = useState('');
	const [photo, setPhoto] = useState(null);

	const selectorMeasurementDetail = createSelector(
		state => state.MeasurementDetail,
		data => ({
			isUpdating: data.isUpdating,
			requireCloseModal: data.requireCloseModal,
		})
	);
	const { isUpdating, requireCloseModal } = useSelector(selectorMeasurementDetail);

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			setErrors({});
			setPhoto(null);
			setMeasurementDate(null);
			setMeasurementTime('');
			setTotalizer('');
		}
	}, [isOpen]);

	// Watch requireCloseModal to close after save
	useEffect(() => {
		if (isOpen && requireCloseModal) {
			dispatch(setRequireCloseModal(false));
			handleToggleModal();
			if (toggleRefetch) {
				setTimeout(() => toggleRefetch(), 100);
			}
		}
	}, [requireCloseModal]);

	const validate = () => {
		const newErrors = {};
		const existingFileId = dataMeasurementDetail?.file_id || null;
		if (!measurementDate) newErrors.measurementDate = 'La fecha es requerida.';
		if (!measurementTime) newErrors.measurementTime = 'La hora es requerida.';
		if (totalizer === '' || totalizer === null || totalizer === undefined) {
			newErrors.totalizer = 'El totalizador es requerido.';
		} else if (isNaN(Number(totalizer))) {
			newErrors.totalizer = 'Debe ser un número válido.';
		}
		if (!(photo instanceof File) && !existingFileId) {
			newErrors.photo = 'La foto es requerida.';
		}
		return newErrors;
	};

	const handleSubmit = async e => {
		e.preventDefault();
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}

		setIsLoading(true);
		setErrors({});

		try {
			const measurementDetailId = dataMeasurementDetail?._id;
			if (!measurementDetailId) {
				setErrors({ submit: 'No se encontró el identificador de la medición.' });
				setIsLoading(false);
				return;
			}

			let fileId = dataMeasurementDetail?.file_id || null;

			// Upload photo if a new file was selected
			if (photo instanceof File) {
				const formData = new FormData();
				formData.append('files', photo);
				const uploadResponse = await uploadFiles({ formData });
				fileId =
					uploadResponse?.[0]?._id ||
					uploadResponse?.[0]?.id ||
					uploadResponse?.data?.[0]?._id ||
					null;
			}

			const payload = {
				measurement_date: measurementDate ? dayjs(measurementDate).format('YYYY-MM-DD') : '',
				measurement_time: measurementTime,
				totalizer: Number(totalizer),
				file_id: fileId,
				_id: measurementDetailId,
			};

			await dispatch(updateMeasurementDetail(payload));
		} catch (err) {
			console.error('Error al guardar detalle de medición:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const isBusy = isLoading || isUpdating;

	return (
		<Form onSubmit={handleSubmit}>
			<ModalBody className='modal-body'>
				<Row className='g-3'>
					<Col lg={6}>
						<div className='mb-0'>
							<Label htmlFor='measurementDate' className='form-label'>
								Fecha de Medición <span className='text-danger'>*</span>
							</Label>
							<DateInput
								id='measurementDate'
								placeholder='Seleccione la fecha'
								value={measurementDate}
								onChange={setMeasurementDate}
								error={errors.measurementDate || null}
								maxDate={new Date()}
								valueFormat='DD-MM-YYYY'
								clearable
								disabled={isBusy}
							/>
						</div>
					</Col>
					<Col lg={6}>
						<CustomTimeInput
							label={
								<>
									Hora de Medición <span className='text-danger'>*</span>
								</>
							}
							placeholder='Seleccione la hora'
							value={measurementTime}
							onChange={e => setMeasurementTime(e.target.value)}
							error={errors.measurementTime || null}
							disabled={isBusy}
						/>
					</Col>
					<Col lg={12}>
						<FormGroup>
							<Label for='totalizer'>
								Totalizador (m³) <span className='text-danger'>*</span>
							</Label>
							<Input
								id='totalizer'
								type='number'
								step='any'
								value={totalizer}
								onChange={e => setTotalizer(e.target.value)}
								invalid={!!errors.totalizer}
								disabled={isBusy}
								placeholder='Ej: 123.45'
							/>
							{errors.totalizer && (
								<FormFeedback>{errors.totalizer}</FormFeedback>
							)}
						</FormGroup>
					</Col>
					<Col lg={12}>
						<FormGroup>
							<Label for='photo'>Foto</Label>
							<Input
								id='photo'
								type='file'
								accept='image/*'
								onChange={e => setPhoto(e.target.files?.[0] || null)}
								invalid={!!errors.photo}
								disabled={isBusy}
							/>
							{dataMeasurementDetail?.file_id && !photo && (
								<small className='text-muted mt-1 d-block'>
									Ya tiene una foto asociada. Selecciona un nuevo archivo para reemplazarla.
								</small>
							)}
							{photo && (
								<small className='text-muted mt-1 d-block'>
									Archivo seleccionado: {photo.name}
								</small>
							)}
							{errors.photo && <FormFeedback>{errors.photo}</FormFeedback>}
						</FormGroup>
					</Col>
					{errors.submit && (
						<Col lg={12}>
							<div className='text-danger small'>{errors.submit}</div>
						</Col>
					)}
				</Row>
			</ModalBody>
			<div className='modal-footer'>
				<div className='hstack gap-2 justify-content-end'>
					<Button
						type='button'
						className='btn-danger btn-label'
						onClick={handleToggleModal}
						disabled={isBusy}
					>
						<i className='icon-x label-icon'></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						disabled={isBusy}
					>
						{isBusy ? (
							<i className='label-icon'>
								<Spinner size='sm' />
							</i>
						) : (
							<i className='ri-save-3-line label-icon'></i>
						)}
						Hacer Medición
					</button>
				</div>
			</div>
		</Form>
	);
};

export default MeasurementDetailsForm;
